import { ContentSnapshot } from "game-common/content/store"
import { InlineContentFiles, StagedContentResources } from "game-common/content/publishing"
import { ContentFileDigest } from "game-common/content/release"
import { canonicalJson } from "game-common/content/resolve"
import { atlasImageDestination } from "game-common/content/atlas"

const verifyBytes = async (bytes: ArrayBuffer, expected: ContentFileDigest, file: string) => {
    if (bytes.byteLength !== expected.size) throw new Error(`Content integrity mismatch: ${file}`)
    if (!globalThis.crypto?.subtle) throw new Error("Content integrity requires Web Crypto (HTTPS or localhost)")
    const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes)
    const hex = Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, "0")).join("")
    if (hex !== expected.sha256) throw new Error(`Content integrity mismatch: ${file}`)
}

const decodeBase64 = (text: string): ArrayBuffer => {
    const binary = atob(text), bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
}

export interface DecodedContentImage { image: CanvasImageSource; close(): void }
export interface ContentAssetBackend {
    fetch(url: string, signal: AbortSignal): Promise<ArrayBuffer>
    decode(bytes: ArrayBuffer, signal: AbortSignal): Promise<DecodedContentImage>
}

/** Uses immutable release URLs, never logical PIXI cache names. Image decoding is part of
 * readiness; a successful HTTP response with corrupt image bytes must fail preparation. */
export const browserContentAssetBackend: ContentAssetBackend = {
    async fetch(url, signal) {
        const response = await fetch(url, { signal })
        if (!response.ok) throw new Error(`Content download failed (${response.status}): ${url}`)
        return response.arrayBuffer()
    },
    async decode(bytes, signal) {
        const url = URL.createObjectURL(new Blob([bytes]))
        const image = new Image()
        try {
            await new Promise<void>((resolve, reject) => {
                const abort = () => { image.src = ""; reject(new Error("Content image decode aborted")) }
                const cleanup = () => signal.removeEventListener("abort", abort)
                image.onload = () => { cleanup(); resolve() }
                image.onerror = () => { cleanup(); reject(new Error("Invalid content image")) }
                signal.addEventListener("abort", abort, { once: true })
                if (signal.aborted) { cleanup(); abort(); return }
                image.src = url
            })
            return { image, close: () => { image.src = "" } }
        } finally { URL.revokeObjectURL(url) }
    },
}

/** The media a client already holds, by digest: one release's integrity inventory. */
export interface ContentBaseline { revision: string; media: Record<string, ContentFileDigest> }

const IMAGE = /\.(png|webp|jpg|jpeg|gif)$/i

// An atlas and its image become ONE texture, so new art behind an unchanged document still means
// staging the document. Every shipped atlas is named `X.png.json` or `X.json` for image `X.png`.
const atlasesDrawing = (file: string): string[] => IMAGE.test(file) ? [`${file}.json`, file.replace(IMAGE, ".json")] : []

export class ContentAssetGeneration implements StagedContentResources {
    readonly bytes = new Map<string, ArrayBuffer>()
    readonly images = new Map<string, DecodedContentImage>()
    readonly documents = new Map<string, unknown>()
    /** Byte-identical to media the client already holds, so not fetched; whoever holds it serves it. */
    readonly inherited = new Set<string>()
    private disposed = false
    private published = false
    constructor(readonly revision: string, private owner: ContentAssets, readonly media?: Record<string, ContentFileDigest>) {}

    activate(): () => void {
        if (this.disposed || this.published) throw new Error("Content asset generation is unavailable")
        const previous = this.owner.active
        this.owner.active = this
        this.published = true
        return () => {
            if (!this.published || this.owner.active !== this || previous?.disposed) throw new Error("Content asset rollback is stale")
            this.owner.active = previous
            this.published = false
        }
    }

    discard(): void {
        if (this.published) throw new Error("Published content assets must be retired by the renderer")
        this.dispose()
    }

    /** Call only after the renderer has released all sprites, atlas views and asynchronous jobs
     * using this generation. The current generation cannot be retired. */
    releaseRetired(): void {
        if (this.owner.active === this) throw new Error("Cannot retire active content assets")
        this.dispose()
    }

    private dispose(): void {
        if (this.disposed) return
        this.disposed = true
        this.images.forEach(image => image.close())
        this.images.clear()
        this.bytes.clear()
        this.documents.clear()
    }
}

/** Stages the release files the client does not already hold, with bounded concurrency. Existing
 * GPU textures are untouched. A live publication changes a handful of files in a release of
 * thousands (one creature: 3 of 4,791, 20KB of 243MB), so anything whose digest matches the
 * active content -- the last activated generation, else the bundled release's own manifest -- is
 * inherited rather than downloaded, hashed, parsed and decoded again.
 * A renderer may create atlas views from these decoded images without another network fetch;
 * it must retain retired generations until all visible sprites/jobs release their references. */
export class ContentAssets {
    active?: ContentAssetGeneration
    private bundled?: ContentBaseline
    constructor(
        private backend = browserContentAssetBackend,
        private assetBase = "",
        private concurrency = 8,
        private bundledRevision: () => string | undefined = () => undefined,
    ) {
        if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error("Invalid content download concurrency")
    }

    private url = (published: string) => `${this.assetBase.replace(/\/$/, "")}${this.assetBase ? "/" : ""}assets/${published}`

    /** No baseline means a full staging: slower, never wrong. */
    private baseline = async (signal: AbortSignal): Promise<ContentBaseline | undefined> => {
        if (this.active) return this.active.media && { revision: this.active.revision, media: this.active.media }
        const revision = this.bundledRevision()
        if (!revision) return undefined
        if (this.bundled?.revision === revision) return this.bundled
        try {
            const manifest = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(await this.backend.fetch(this.url(`content/${revision}/manifest.json`), signal)))
            if (manifest?.revision !== revision || !manifest.integrity?.media) return undefined
            return (this.bundled = { revision, media: manifest.integrity.media })
        } catch { return undefined }
    }

    /** `canReuse` lets the renderer refuse to inherit a file it has no loaded copy of. `inline` holds
     * files the server sent because no web host serves them; they are checked like downloads. */
    stage = async (snapshot: ContentSnapshot, signal: AbortSignal, canReuse: (file: string) => boolean = () => true, inline?: InlineContentFiles): Promise<ContentAssetGeneration> => {
        const integrity = snapshot.manifest.integrity
        if (integrity) await verifyBytes(new TextEncoder().encode(canonicalJson(snapshot.catalog)).buffer, integrity.catalog, "catalog.json")
        const generation = new ContentAssetGeneration(snapshot.catalog.revision, this, integrity?.media)
        const controller = new AbortController()
        const abort = () => controller.abort()
        signal.addEventListener("abort", abort, { once: true })
        if (signal.aborted) abort()
        const fetchAll = async (files: string[]) => {
            let cursor = 0
            const workers = Array.from({ length: Math.min(this.concurrency, files.length) }, async () => {
                while (cursor < files.length) {
                    if (controller.signal.aborted) throw new Error("Content download aborted")
                    const file = files[cursor++]
                    try {
                        const sent = inline && Object.prototype.hasOwnProperty.call(inline, file) ? inline[file] : undefined
                        const bytes = typeof sent === "string" ? decodeBase64(sent) : await this.backend.fetch(this.url(snapshot.catalog.assets[file]), controller.signal)
                        if (integrity) await verifyBytes(bytes, integrity.media[file], file)
                        if (controller.signal.aborted) throw new Error("Content download aborted")
                        generation.bytes.set(file, bytes)
                        if (/\.json$/i.test(file)) generation.documents.set(file, JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)))
                        if (IMAGE.test(file)) {
                            const image = await this.backend.decode(bytes, controller.signal)
                            if (controller.signal.aborted) { image.close(); throw new Error("Content decode aborted") }
                            generation.images.set(file, image)
                        }
                    } catch (error) { controller.abort(); throw error }
                }
            })
            // Await every worker before cleanup: late decodes cannot resurrect a discarded generation.
            const failed = (await Promise.allSettled(workers)).find(result => result.status === "rejected") as PromiseRejectedResult | undefined
            if (failed) throw failed.reason
        }
        try {
            const media = snapshot.manifest.media, listed = new Set(media)
            const baseline = integrity && !controller.signal.aborted ? await this.baseline(controller.signal) : undefined
            const held = (file: string) => {
                const before = baseline?.media[file], after = integrity?.media[file]
                return !!before && !!after && before.sha256 === after.sha256 && before.size === after.size && canReuse(file)
            }
            const wanted = new Set(media.filter(file => !held(file)))
            for (const file of [...wanted]) for (const atlas of atlasesDrawing(file)) if (listed.has(atlas)) wanted.add(atlas)
            await fetchAll(media.filter(file => wanted.has(file)))
            // ...and the other way round: a staged atlas needs its image even when only the document changed.
            const images = new Set<string>()
            for (const [file, document] of generation.documents) {
                const image = (document as any)?.frames && atlasImageDestination(file, (document as any).meta?.image)
                if (image && listed.has(image) && !wanted.has(image)) images.add(image)
            }
            images.forEach(image => wanted.add(image))
            await fetchAll([...images])
            for (const file of media) if (!wanted.has(file)) generation.inherited.add(file)
        } catch (error) {
            signal.removeEventListener("abort", abort)
            generation.discard()
            throw error
        }
        signal.removeEventListener("abort", abort)
        if (controller.signal.aborted) { generation.discard(); throw new Error("Content download aborted") }
        return generation
    }
}
